import React, { useState } from "react";
import { Clock } from "@phosphor-icons/react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CLOCKS = [["eastern", "Eastern time"], ["local", "Visitor's local time"]];
const hourLabel = (h) => `${h % 12 || 12}${h < 12 ? "am" : "pm"}`;
const leadsWord = (n) => `${n} lead${n === 1 ? "" : "s"}`;
const slotLabel = (c) => `${DAYS[c.day]} ${hourLabel(c.hour)}–${hourLabel((c.hour + 1) % 24)}`;

function cellStyle(c, max) {
  if (!c.sold) return c.generated ? { backgroundColor: "#e4e4e7" } : {};
  const r = c.sold / (max || 1);
  return { backgroundColor: `rgba(5, 150, 105, ${0.25 + 0.75 * r})`, color: r > 0.45 ? "#fff" : "#064e3b" };
}

const Grid = ({ view }) => (
  <div className="overflow-x-auto">
    <div className="min-w-[680px] grid gap-px" style={{ gridTemplateColumns: "40px repeat(24, minmax(0, 1fr))" }} data-testid="best-hours-grid">
      <div />
      {Array.from({ length: 24 }, (_, h) => (
        <div key={h} className="text-[9px] text-zinc-500 text-center">{h % 3 === 0 ? hourLabel(h) : ""}</div>
      ))}
      {view.grid.map((row, d) => (
        <React.Fragment key={d}>
          <div className="text-[10px] uppercase tracking-widest self-center">{DAYS[d]}</div>
          {row.map((c, h) => (
            <div key={h} title={`${DAYS[d]} ${hourLabel(h)}: ${c.sold} sold of ${leadsWord(c.generated)}`} style={cellStyle(c, view.max_sold)}
                 className="h-6 border border-zinc-200 text-[9px] font-bold flex items-center justify-center transition-transform hover:scale-110"
                 data-testid={`best-hours-cell-${d}-${h}`}>
              {c.sold || ""}
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>
  </div>
);

const TopSlots = ({ view }) => (
  <div className="flex flex-col gap-2" data-testid="best-hours-top">
    <div className="label-mono">Top times</div>
    {view.top.length ? view.top.map((c, i) => (
      <div key={`${c.day}-${c.hour}`} className="flex items-center justify-between gap-3 border-2 border-black px-2 py-1.5 text-xs" data-testid={`best-hours-top-${i}`}>
        <span className="font-bold">{i + 1}. {slotLabel(c)}</span>
        <span className="font-mono">{c.sold} sold · {leadsWord(c.generated)}</span>
      </div>
    )) : <div className="text-xs text-zinc-500" data-testid="best-hours-empty">No sold leads in this date range yet.</div>}
  </div>
);

const SPLITS = [["weekday", "Weekdays (Mon–Fri)", [0, 1, 2, 3, 4]], ["weekend", "Weekends (Sat–Sun)", [5, 6]]];
const share = (n) => `${Math.round(n * 100)}%`;

function weekSplit(view) {
  const rows = SPLITS.map(([key, label, days]) => {
    const t = days.reduce((acc, d) => {
      view.grid[d].forEach((c) => { acc.sold += c.sold; acc.generated += c.generated; });
      return acc;
    }, { sold: 0, generated: 0 });
    return { key, label, ...t, perDay: t.sold / days.length, rate: t.generated ? t.sold / t.generated : 0 };
  });
  const total = rows[0].sold + rows[1].sold;
  const wdPct = total ? Math.round((rows[0].sold / total) * 100) : 0;
  rows[0].pct = wdPct;
  rows[1].pct = total ? 100 - wdPct : 0;
  return rows;
}

function splitAdvice([wd, we]) {
  if (!(wd.sold + we.sold)) return null;
  let perDay = "Weekend days and weekdays sell about the same per day.";
  if (wd.perDay && we.perDay) {
    const r = we.perDay / wd.perDay;
    if (r >= 1.15) perDay = `A weekend day sells ${r.toFixed(1)}× what a weekday does.`;
    else if (r <= 0.87) perDay = `A weekday sells ${(1 / r).toFixed(1)}× what a weekend day does.`;
  } else {
    perDay = wd.perDay ? "No weekend sales yet in this range." : "No weekday sales yet in this range.";
  }
  return `Match your sold leads: about ${wd.pct}% of your weekly budget on weekdays and ${we.pct}% on weekends. ${perDay}`;
}

const WeekSplit = ({ view }) => {
  const rows = weekSplit(view);
  const advice = splitAdvice(rows);
  return (
    <div className="mt-4 border-t-2 border-black pt-3" data-testid="week-split">
      <div className="label-mono mb-2">Weekdays vs weekends</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((r) => (
          <div key={r.key} className="border-2 border-black p-2.5" data-testid={`week-split-${r.key}`}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs font-bold">{r.label}</span>
              <span className="font-chivo font-black text-xl" data-testid={`week-split-${r.key}-sold`}>{r.sold} sold</span>
            </div>
            <div className="h-2 bg-zinc-200 mt-1.5"><div className="h-2 bg-emerald-600 transition-[width] duration-500" style={{ width: `${r.pct}%` }} /></div>
            <div className="text-[11px] text-zinc-700 mt-1.5 flex flex-wrap gap-x-4">
              <span data-testid={`week-split-${r.key}-share`}>{r.pct}% of sold</span>
              <span data-testid={`week-split-${r.key}-per-day`}>{r.perDay.toFixed(1)} sold per day</span>
              <span data-testid={`week-split-${r.key}-rate`}>{share(r.rate)} of {leadsWord(r.generated)} sold</span>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs mt-2" data-testid="week-split-advice">{advice || "No sold leads in this date range yet."}</p>
    </div>
  );
};

const Summary = ({ view, clock }) => (
  <div className="text-xs mb-3 flex flex-wrap gap-x-5 gap-y-1" data-testid="best-hours-summary">
    <span>Best day: <b data-testid="best-hours-best-day">{view.best_day ? `${DAYS[view.best_day.day]} (${view.best_day.sold} sold)` : "—"}</b></span>
    <span>Best hour: <b data-testid="best-hours-best-hour">{view.best_hour ? `${hourLabel(view.best_hour.hour)} (${view.best_hour.sold} sold)` : "—"}</b></span>
    {clock === "local" && view.no_state > 0 && (
      <span className="text-zinc-500" data-testid="best-hours-no-state">{view.no_state} lead(s) without a state are shown on Eastern time</span>
    )}
  </div>
);

export const BestHours = ({ data }) => {
  const [clock, setClock] = useState("eastern");
  const view = data?.best_hours?.[clock];
  if (!view) return null;
  return (
    <section className="brut-card bg-white p-3 mb-6" data-testid="best-hours">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Clock size={16} weight="bold" />
        <h2 className="font-chivo font-black uppercase tracking-tight text-base">Best days &amp; hours</h2>
        <span className="text-xs text-zinc-600">ranked by leads sold · follows your filters</span>
        <div className="sm:ml-auto flex gap-1.5">
          {CLOCKS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setClock(k)} data-testid={`best-hours-clock-${k}`}
                    className={`border-2 border-black px-2.5 py-1 text-[10px] uppercase tracking-widest transition-colors ${clock === k ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <Summary view={view} clock={clock} />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">
        <Grid view={view} />
        <TopSlots view={view} />
      </div>
      <WeekSplit view={view} />
      <div className="text-[10px] text-zinc-500 mt-3">Darker green = more leads sold · grey = leads came in but none sold yet · hover a square for details<span className="sm:hidden"> · scroll sideways for later hours</span></div>
    </section>
  );
};
